import { btnHtml } from "./kit";
import { uiUrl } from "./portraits";
import { TITLE_STILL } from "./title";

/** What the boot-error screen can do: start again, and hand the diagnostics report on (infra/diagnostics.ts). */
export interface BootErrorActions {
  retry: () => void;
  copyDiagnostics: () => Promise<"shared" | "copied" | "failed">;
}

/**
 * The player-facing reason for a failed start. A page that cannot create a WebGL context (3D turned off, or iOS out of
 * live contexts) is the one cause a player can act on; anything else is "try again" plus the technical line.
 */
export function bootErrorReason(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  return /webgl|context/i.test(msg)
    ? "The battlefield wouldn't draw. Close a few tabs or apps, or restart the browser, then try again."
    : "The gates jammed on the way in. Try again: that usually frees them.";
}

/**
 * The game could not start (RC1 A5: no WebGL, a failed asset or save load): a message with RETRY and COPY
 * DIAGNOSTICS in place of a blank or frozen page. It replaces the loading splash. Built from plain DOM so it works
 * whatever part of the boot failed.
 */
export function showBootError(parent: HTMLElement, err: unknown, actions: BootErrorActions): HTMLElement {
  // it replaces the boot's title screen (its loading plate) and the page's own first-paint splash
  for (const old of parent.querySelectorAll(".screen.title, .screen.boot-error")) old.remove();
  document.getElementById("boot-splash")?.remove();
  const root = document.createElement("div");
  root.className = "screen loading boot-error";
  root.style.backgroundImage = `url("${uiUrl(TITLE_STILL)}"), var(--dusk-sky)`;
  root.setAttribute("role", "alert");
  root.innerHTML = `
    <div class="logo"><img decoding="sync" src="${uiUrl("title/logo.png")}" alt="Highmarch"></div>
    <div class="panel">
      <h2>THE GATES WON'T OPEN</h2>
      <p class="why"></p>
      <p class="detail"></p>
      <div class="actions">${btnHtml("RETRY", "gold", { className: "retry" })}${btnHtml("COPY REPORT", "dark", { className: "copy" })}</div>
    </div>`;
  root.querySelector(".why")!.textContent = bootErrorReason(err);
  root.querySelector(".detail")!.textContent = err instanceof Error ? err.message : String(err);
  root.querySelector<HTMLButtonElement>(".retry")!.onclick = () => actions.retry();
  const copy = root.querySelector<HTMLButtonElement>(".copy")!;
  copy.onclick = async () => {
    const how = await actions.copyDiagnostics().catch(() => "failed" as const);
    copy.querySelector(".disp")!.textContent = how === "copied" ? "COPIED" : how === "shared" ? "SHARED" : "COULDN'T COPY";
  };
  parent.append(root);
  return root;
}
